import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { createCourse, listAllCourses, type CourseRow } from '../../lib/adminApi';
import { friendlyError } from '../../lib/supabase';
import { formatPrice } from '../../lib/progress';
import type { CourseStatus } from '../../lib/types';
import { Badge, Button, Callout, Spinner, TextField } from '../../components/ui';

export function StatusBadge({ status }: { status: CourseStatus }) {
  const tone = status === 'published' ? 'success' : status === 'draft' ? 'warning' : status === 'private' ? 'brand' : 'neutral';
  return <Badge tone={tone}>{status[0]!.toUpperCase() + status.slice(1)}</Badge>;
}

export function AdminCourses() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<CourseRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { listAllCourses().then(setRows).catch((e) => setError(friendlyError(e))); }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try { const c = await createCourse(title.trim()); navigate(`/admin/courses/${c.id}`); }
    catch (err) { setError(friendlyError(err)); setBusy(false); }
  }

  return (
    <>
      <h1 style={{ margin: 0, fontSize: 28, lineHeight: '36px', fontWeight: 400, color: 'var(--brand)' }}>Courses</h1>
      {error && <Callout variant="error">{error}</Callout>}
      {isAdmin && (
        <form className="row box" onSubmit={create} style={{ alignItems: 'flex-end' }}>
          <TextField label="New course title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. California Dryland Stream Delineation" />
          <Button type="submit" icon="book" busy={busy} disabled={!title.trim()}>Create course</Button>
        </form>
      )}
      {!rows ? <Spinner /> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Course</th><th>Status</th><th className="num">Price</th><th className="num">Enrolled</th><th className="num">Completed</th><th>Last edited</th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td><Link to={`/admin/courses/${c.id}`} style={{ fontWeight: 600 }}>{c.title}</Link>{c.is_demo && <> <Badge tone="warning">Demo</Badge></>}</td>
                  <td><StatusBadge status={c.status} /></td>
                  <td className="num">{c.price_cents ? formatPrice(c.price_cents, c.currency) : '—'}</td>
                  <td className="num">{c.enrolled}</td>
                  <td className="num">{c.completed}</td>
                  <td>{new Date(c.updated_at).toLocaleDateString('en-US', { dateStyle: 'medium' })}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="muted">No courses yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
